import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { Lijst } from '@xxtract/tabelfilters';
import { useClientScores, useClientsEnrichment } from '../hooks/useClientScores';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { ariaSort, Filterbalk, SorteerbareKop, useStartadres, useTabelData, useTabelStand } from '@/components/tabel';
import { SkeletonTable } from '@/components/feedback/SkeletonCard';
import { TrendBadge } from '@/features/data-quality/components/TrendBadge';
import type { ClientSummary, ClientTrend } from '../completeness.api';

/** Business ordering for the trend column (h4-4): worst-first when sorted ascending. */
const TREND_SORT_WEIGHT: Record<ClientTrend, number> = { declining: 0, stable: 1, improving: 2 };

interface EnrichedClient extends ClientSummary {
  /** h4-4/FR28. Null = not yet loaded OR procesManagement unavailable (NFR4 — shown as a dash + tooltip, never blocks the row). */
  openOrderCount: number | null;
  trend: ClientTrend | null;
}

type ClientStatus = 'good' | 'attention' | 'actionNeeded';
const STATUS_LABEL_KEYS: Record<ClientStatus, string> = {
  good: 'clients.statusGood',
  attention: 'clients.statusAttention',
  actionNeeded: 'clients.statusActionNeeded',
};

function statusVan(score: number): ClientStatus {
  if (score >= 80) return 'good';
  if (score >= 50) return 'attention';
  return 'actionNeeded';
}

/**
 * spec-tabelfilters story 4: de kolommen van het klantoverzicht (§2). De klantkolom filtert op
 * "naam (GLN)", zodat zoeken op GLN blijft werken; trend is een getal in de zakelijke volgorde (h4-4).
 */
export const KLANT_LIJST: Lijst<EnrichedClient> = {
  kolommen: [
    { sleutel: 'klant', kopSleutel: 'clients.clientName', type: 'tekst', waarde: (c) => (c.name ? `${c.name} (${c.glnNumber})` : c.glnNumber) },
    { sleutel: 'producten', kopSleutel: 'clients.productCount', type: 'getal', waarde: (c) => c.productCount },
    { sleutel: 'compleetheid', kopSleutel: 'clients.completeness', type: 'getal', waarde: (c) => c.overallScore },
    // Onbekend (procesManagement onbereikbaar of nog niet geladen) is `null`: in beide richtingen onderaan.
    { sleutel: 'openOrders', kopSleutel: 'clients.openOrders', type: 'getal', waarde: (c) => c.openOrderCount },
    { sleutel: 'trend', kopSleutel: 'clients.trend', type: 'getal', waarde: (c) => (c.trend ? TREND_SORT_WEIGHT[c.trend] : null) },
    { sleutel: 'status', kopSleutel: 'clients.status', type: 'keuzelijst', waarde: (c) => statusVan(c.overallScore), waardeLabel: (w, t) => t(STATUS_LABEL_KEYS[w as ClientStatus] ?? w), vasteWaarden: Object.keys(STATUS_LABEL_KEYS) },
  ],
  standaardSortering: () => [{ sleutel: 'compleetheid', richting: 'op' }],
};
const FILTER_TEST_ID = 'clients';
const STAND_SLEUTEL = 'klanten';
const STARTADRES = [{ lijst: KLANT_LIJST, standSleutel: STAND_SLEUTEL }];

const STATUS_STIJL: Record<ClientStatus, { color: string; bg: string; icon: string }> = {
  good: { color: 'var(--success-dark)', bg: 'var(--success-light)', icon: 'check_circle' },
  attention: { color: 'var(--warning)', bg: 'var(--warning-light)', icon: 'warning' },
  actionNeeded: { color: 'var(--destructive)', bg: 'var(--destructive-light)', icon: 'error' },
};

function getStatusInfo(score: number, t: (key: string) => string) {
  const status = statusVan(score);
  return { label: t(STATUS_LABEL_KEYS[status]), ...STATUS_STIJL[status] };
}

export function ClientOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useClientScores();
  // h4-4: an independent query — a degraded/slow enrichment never blocks the base client list.
  const { data: enrichmentData, isLoading: enrichmentLoading } = useClientsEnrichment();
  const ordersDegraded = enrichmentData?.meta.ordersDegraded ?? false;
  const enrichmentByGln = useMemo(() => {
    const map = new Map<string, { openOrderCount: number | null; trend: ClientTrend | null }>();
    for (const row of enrichmentData?.data ?? []) {
      map.set(row.gln, { openOrderCount: row.openOrderCount, trend: row.trend });
    }
    return map;
  }, [enrichmentData]);

  const clients: EnrichedClient[] = useMemo(() => (data?.data ?? []).map((client) => {
    const enrichment = enrichmentByGln.get(client.glnNumber);
    // IMPORTANT: an explicit `openOrderCount: null` from the backend means "degraded" and must
    // be preserved as-is — `enrichment.openOrderCount ?? 0` would be wrong here (`??` treats
    // that null as "missing" and silently claims zero open orders during a real outage).
    // Only fall back to a default when the enrichment row itself has not arrived yet.
    if (enrichment) {
      return { ...client, openOrderCount: enrichment.openOrderCount, trend: enrichment.trend };
    }
    return { ...client, openOrderCount: enrichmentLoading ? null : 0, trend: null };
  }), [data, enrichmentByGln, enrichmentLoading]);

  const kopRef = useRef<HTMLHeadingElement>(null);
  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTADRES);
  const stand = useTabelStand(KLANT_LIJST, STAND_SLEUTEL, { start: start[STAND_SLEUTEL] });
  const context = useMemo(() => ({ t: (sleutel: string) => t(sleutel) }), [t]);
  const tabel = useTabelData(clients, KLANT_LIJST, stand, context);
  const sortedClients = tabel.rijen;
  const standaard = KLANT_LIJST.standaardSortering?.(context);

  // Summary stats
  const totalClients = clients.length;
  const avgScore = totalClients > 0
    ? Math.round(clients.reduce((sum, c) => sum + c.overallScore, 0) / totalClients)
    : 0;
  const needsAttention = clients.filter((c) => c.overallScore < 50).length;
  const fullyComplete = clients.filter((c) => c.overallScore === 100).length;

  if (isLoading) {
    return <SkeletonTable rows={6} />;
  }

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  return (
    <div data-testid="client-overview">
      {/* Summary Cards */}
      <div className="mb-6 grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title={t('clients.totalClients')}
          value={String(totalClients)}
          icon="groups"
          iconColor="var(--primary)"
          iconBg="var(--primary-light)"
        />
        <SummaryCard
          title={t('clients.avgCompleteness')}
          value={`${avgScore}%`}
          icon="trending_up"
          iconColor="var(--success-dark)"
          iconBg="var(--success-light)"
        />
        <SummaryCard
          title={t('clients.needsAttention')}
          value={String(needsAttention)}
          subtitle={t('clients.scoreBelow50')}
          icon="warning"
          iconColor="var(--warning)"
          iconBg="var(--warning-light)"
        />
        <SummaryCard
          title={t('clients.fullyComplete')}
          value={String(fullyComplete)}
          subtitle={t('clients.score100')}
          icon="check_circle"
          iconColor="var(--success-dark)"
          iconBg="var(--success-light)"
        />
      </div>

      <FilterRejectedMessage
        baseTestId={FILTER_TEST_ID}
        focusNaWissen={kopRef}
        linkGeweigerd={geweigerd}
        onClear={() => {
          stand.reset();
          wisGeweigerd();
        }}
      />

      {/* Table section — geen overflow-hidden: de filterpopups moeten eruit kunnen steken */}
      <div
        className="rounded-xl"
        style={{
          backgroundColor: 'var(--background)',
          border: '1px solid var(--border-soft)',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        {/* Table header with title and the central filter bar */}
        <div className="flex flex-col gap-3 px-5 py-4" style={{ borderBottom: '1px solid var(--border-soft)' }}>
          <h2 className="text-sm font-semibold" ref={kopRef} style={{ color: 'var(--foreground)' }} tabIndex={-1}>
            {t('clients.allClients')}
          </h2>
          <Filterbalk context={context} data={tabel} lijst={KLANT_LIJST} stand={stand} />
        </div>

        {/* Table */}
        {sortedClients.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center">
            <span
              className="material-symbols-outlined mb-3"
              style={{ fontSize: '40px', color: 'var(--foreground-muted)', opacity: 0.5 }}
              aria-hidden="true"
            >
              inbox
            </span>
            <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
              {t('common.noResults')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="client-table">
            <thead>
              <tr style={{ backgroundColor: 'var(--background-muted)' }}>
                {KLANT_LIJST.kolommen.map((k) => (
                  <th
                    aria-sort={ariaSort(stand.sortering, k.sleutel)}
                    className="px-4 py-2.5 text-left text-[0.6875rem] font-semibold uppercase tracking-wider"
                    data-testid={`sort-${k.sleutel}`}
                    key={k.sleutel}
                    scope="col"
                    style={{ color: 'var(--foreground-muted)' }}
                  >
                    <SorteerbareKop kolom={k} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} standaard={standaard} />
                  </th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {sortedClients.map((client) => (
                <ClientRow
                  key={client.id}
                  client={client}
                  ordersDegraded={ordersDegraded}
                  onClick={() => navigate(`/data-quality/${client.glnNumber}`)}
                />
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        {sortedClients.length > 0 && (
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{ borderTop: '1px solid var(--border-soft)' }}
          >
            <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
              {t('clients.clientsRange', {
                from: 1,
                to: sortedClients.length,
                total: tabel.totaal,
              })}
            </span>
            <div className="flex gap-2">
              <button
                disabled
                className="text-xs font-medium disabled:opacity-40"
                style={{ color: 'var(--foreground-muted)' }}
              >
                {t('common.previous')}
              </button>
              <button
                disabled
                className="text-xs font-medium disabled:opacity-40"
                style={{ color: 'var(--foreground-muted)' }}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
  icon,
  iconColor,
  iconBg,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: string;
  iconColor: string;
  iconBg: string;
}) {
  return (
    <div
      className="flex items-start justify-between rounded-xl px-5 py-4"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border-soft)',
        boxShadow: 'var(--card-shadow)',
      }}
    >
      <div>
        <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{title}</p>
        <p className="mt-1 text-2xl font-bold" style={{ color: 'var(--foreground)' }}>{value}</p>
        {subtitle && (
          <p className="mt-0.5 text-[0.6875rem]" style={{ color: 'var(--foreground-muted)' }}>{subtitle}</p>
        )}
      </div>
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg"
        style={{ backgroundColor: iconBg }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '20px', color: iconColor }}>
          {icon}
        </span>
      </div>
    </div>
  );
}

function ClientRow({
  client,
  ordersDegraded,
  onClick,
}: {
  client: EnrichedClient;
  ordersDegraded: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const status = getStatusInfo(client.overallScore, t);
  const scoreColor = status.color;
  const scoreBgColor = status.bg;
  const rounded = Math.round(client.overallScore);

  return (
    <tr
      data-testid={`client-row-${client.id}`}
      className="cursor-pointer transition-colors duration-100 hover:bg-[var(--background-muted)]"
      style={{ borderBottom: '1px solid var(--border-soft)' }}
      role="button"
      tabIndex={0}
      aria-label={client.name || client.glnNumber}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Client name + GLN */}
      <td className="px-4 py-3">
        <div>
          <span className="text-[0.8125rem] font-medium" style={{ color: 'var(--foreground)' }}>
            {client.name || client.glnNumber}
          </span>
          {client.name && (
            <span className="block text-[0.6875rem]" style={{ color: 'var(--foreground-muted)', opacity: 0.7 }}>
              {client.glnNumber}
            </span>
          )}
        </div>
      </td>

      {/* Products */}
      <td className="px-4 py-3">
        <span className="text-[0.8125rem]" style={{ color: 'var(--foreground)' }}>
          {client.productCount.toLocaleString('nl-NL')}
        </span>
      </td>

      {/* Completeness - icon + bar + percentage */}
      <td className="px-4 py-3" style={{ minWidth: '180px' }}>
        <div className="flex items-center gap-2">
          <span
            className="material-symbols-outlined"
            style={{ fontSize: '16px', color: scoreColor }}
            aria-hidden="true"
          >
            {status.icon}
          </span>
          <div
            className="relative h-2 w-24 overflow-hidden rounded-full"
            role="progressbar"
            aria-valuenow={rounded}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{ backgroundColor: scoreBgColor }}
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${client.overallScore}%`, backgroundColor: scoreColor }}
            />
          </div>
          <span className="text-xs font-semibold" style={{ color: scoreColor }}>
            {rounded}%
          </span>
        </div>
      </td>

      {/* h4-4/FR28: openstaande orders — leeg + melding wanneer procesManagement onbereikbaar is (NFR4) */}
      <td className="px-4 py-3">
        {client.openOrderCount != null ? (
          <span className="text-[0.8125rem]" style={{ color: 'var(--foreground)' }}>
            {client.openOrderCount.toLocaleString('nl-NL')}
          </span>
        ) : (
          <span
            className="text-xs"
            style={{ color: 'var(--foreground-muted)' }}
            title={ordersDegraded ? t('clients.ordersUnavailable') : undefined}
          >
            {t('common.notAvailable')}
          </span>
        )}
      </td>

      {/* h4-4/FR23/FR28: completeness-trend */}
      <td className="px-4 py-3">
        {client.trend ? <TrendBadge trend={client.trend} /> : (
          <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{t('common.notAvailable')}</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1 text-xs font-medium"
          style={{ color: status.color }}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: status.color }}
          />
          {status.label}
        </span>
      </td>

      {/* Chevron */}
      <td className="px-2 py-3">
        <span
          className="material-symbols-outlined"
          style={{ fontSize: '18px', color: 'var(--foreground-muted)', opacity: 0.5 }}
          aria-hidden="true"
        >
          chevron_right
        </span>
      </td>
    </tr>
  );
}
